import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { samplePages, volumeColors } from '../data/content';
import type { SamplePage } from '../data/content';
import { CtaButton } from './CtaButton';
export function LookInside() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(samplePages.length - 1, index));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let minDistance = Infinity;
        Array.from(track.children).forEach((child, index) => {
          const el = child as HTMLElement;
          const distance = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
          if (distance < minDistance) {
            minDistance = distance;
            closest = index;
          }
        });
        setActive(closest);
      });
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  const current = samplePages[active];
  return <section aria-roledescription="carousel" aria-label="Sample pages from the CAREER READY pack" className="w-full overflow-hidden bg-ink py-20 text-white sm:py-28" data-id="element-162" data-mp="3seucpxu8tgq1">
      <div className="mx-auto max-w-3xl px-5 text-center" data-id="element-163" data-mp="2zrgnklwsdpgh">
        <h2 className="font-display text-[2rem] font-black leading-[1.08] tracking-tight sm:text-6xl" data-id="element-164" data-mp="13pdb3lttcftg">
          Take a Look <span className="text-sun" data-id="element-165" data-mp="18mwnshav4kja">Inside.</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg" data-id="element-166" data-mp="1den0gtx9vkdv">
          See What You Can Bring Into Your Classroom.
        </p>

        <div role="tablist" aria-label="Choose a volume" className="mt-8 inline-flex max-w-full overflow-x-auto rounded-full bg-white/10 p-1 no-scrollbar" data-id="element-167" data-mp="1hfgpjqmgb58c">
          {samplePages.map((page, index) => {
          const isActive = index === active;
          return <button key={page.tab} type="button" role="tab" aria-selected={isActive} aria-label={page.caption} onClick={() => scrollToIndex(index)} className={`whitespace-nowrap rounded-full px-2.5 py-2 text-xs font-extrabold tracking-wide transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sun sm:px-5 sm:text-sm ${isActive ? 'bg-sun text-ink' : 'text-white/60 hover:text-white'}`} data-id="element-168" data-mp="tq5umprs8524" data-mpi={index}>
                {page.tab}
              </button>;
        })}
        </div>
      </div>

      <div className="relative mt-12" data-id="element-169" data-mp="2f9hs9dz7g3qp">
        <div ref={trackRef} className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto px-[7%] pb-2 sm:gap-8 sm:px-[20%] lg:px-[30%]" data-id="element-170" data-mp="2faj9y1aq7ohe">
          {samplePages.map((page, index) => {
          const isActive = index === active;
          return <figure key={page.tab} role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${samplePages.length}: ${page.caption}`} className="w-[86%] shrink-0 snap-center sm:w-[60%] lg:w-[40%]" data-id="element-171" data-mp="21iw71ima3hpn" data-mpi={index}>
                <button type="button" onClick={() => scrollToIndex(index)} tabIndex={isActive ? -1 : 0} aria-label={`Show ${page.caption} sample page`} className={`block w-full overflow-hidden rounded-2xl bg-white p-1.5 text-left shadow-lg transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] focus:outline-none focus-visible:ring-4 focus-visible:ring-sun/60 ${isActive ? 'scale-100 opacity-100' : 'scale-[0.92] opacity-40'}`} data-id="element-172" data-mp="1rl88sipgrs2q">
                  {page.image ? <img src={page.image} alt={`${page.caption} sample page`} className="aspect-[4/5] w-full rounded-xl object-cover object-top" loading="lazy" draggable={false} data-id="element-173" data-mp="3tynmh8w8nr5h" /> : <PagePreview page={page} index={index} data-id="element-174" data-mp="3fyv0trzkk14d" />}
                </button>
              </figure>;
        })}
        </div>

        <button type="button" onClick={() => scrollToIndex(active - 1)} disabled={active === 0} aria-label="Previous page" className="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border-2 border-ink bg-sun text-ink shadow-lg transition-[opacity,transform,background-color] duration-150 hover:scale-105 hover:bg-sun-deep active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40 disabled:pointer-events-none disabled:opacity-30 sm:left-4 sm:h-12 sm:w-12 lg:left-[calc(30%-4.5rem)]" data-id="element-175" data-mp="369bfmfodvv6c">
          <ChevronLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" data-id="element-176" data-mp="2s8quvdywrpa7" />
        </button>
        <button type="button" onClick={() => scrollToIndex(active + 1)} disabled={active === samplePages.length - 1} aria-label="Next page" className="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border-2 border-ink bg-sun text-ink shadow-lg transition-[opacity,transform,background-color] duration-150 hover:scale-105 hover:bg-sun-deep active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40 disabled:pointer-events-none disabled:opacity-30 sm:right-4 sm:h-12 sm:w-12 lg:right-[calc(30%-4.5rem)]" data-id="element-177" data-mp="28o0g72acpwxm">
          <ChevronRightIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" data-id="element-178" data-mp="31b4g4sul7l2j" />
        </button>
      </div>

      <div className="mx-auto mt-8 max-w-md px-5 text-center" aria-live="polite" data-id="element-179" data-mp="2we4i957w5omi">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-sun" data-id="element-180" data-mp="3advtiy70wqwt">
          {current.volume}
        </p>
        <h3 className="mt-2 font-display text-xl font-bold sm:text-2xl" data-id="element-181" data-mp="35by1pfkk4ns9">
          {current.caption}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-white/70" data-id="element-182" data-mp="22ys4os0460up">
          {current.text}
        </p>

        <div className="mt-6 flex items-center justify-center gap-2 sm:hidden" data-id="element-183" data-mp="2c1b4f38lrxcq">
          {samplePages.map((page, index) => <span key={page.tab} aria-hidden="true" className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ${index === active ? 'w-6 bg-sun' : 'w-1.5 bg-white/30'}`} data-id="element-184" data-mp="2gsjcnvy75cob" data-mpi={index} />)}
        </div>
      </div>

      <div className="mx-auto mt-16 max-w-2xl px-5 text-center" data-id="element-185" data-mp="2qpe7a3rcmsxb">
        <p className="font-display text-2xl font-black leading-tight tracking-tight sm:text-4xl" data-id="element-186" data-mp="2vpacg6bs8qeg">
          Explore the Topic. Choose the Activity.{' '}
          <span className="text-sun" data-id="element-187" data-mp="2zqc3hzvjpw5c">Bring It to Class.</span>
        </p>
        <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-white/70" data-id="element-188" data-mp="11c12kdfbr2vo">
          The value isn’t just having more pages. It’s having a clear next step
          when you sit down to plan.
        </p>
        <CtaButton href="#offer" className="mt-8" data-id="element-189" data-mp="xu3l4wd601hz" />
      </div>
    </section>;
}
function PagePreview({
  page,
  index
}: {
  page: SamplePage;
  index: number;
}) {
  const color = volumeColors[index % volumeColors.length];
  return <div className="flex aspect-[4/5] w-full flex-col overflow-hidden rounded-xl border-2 border-ink bg-paper" data-id="element-190" data-mp="o1foq2q3jzjs">
      <div className={`${color.bg} ${color.fg} flex items-end gap-3 border-b-2 border-ink px-5 py-4`} data-id="element-191" data-mp="k03x5rnnx48y">
        <span className="font-display text-5xl font-black leading-[0.8]" data-id="element-192" data-mp="1bt39e2wrmedm">
          {index + 1}
        </span>
        <div data-id="element-193" data-mp="17r9a9z27xifk">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] opacity-80" data-id="element-194" data-mp="17qzsuzv8ojlh">
            CAREER READY™ · {page.volume}
          </p>
          <p className="mt-1 font-display text-base font-black leading-tight" data-id="element-195" data-mp="12s53tg6ohysx">
            {page.caption}
          </p>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-4 px-5 py-5" data-id="element-196" data-mp="3nix59oxo90vq">
        <p className="text-xs leading-relaxed text-ink-soft" data-id="element-197" data-mp="3nkqf1d3l017y">{page.text}</p>
        <div className="space-y-3" aria-hidden="true" data-id="element-198" data-mp="1repsegw0dad6">
          {[0, 1, 2].map(row => <div key={row} className="flex items-center gap-3" data-id="element-199" data-mp="1vhckmiwlcxaw">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-ink/20 text-[10px] font-bold text-ink/40" data-id="element-200" data-mp="1hhbasdycmfja">
                {row + 1}
              </span>
              <span className="h-px flex-1 bg-ink/15" data-id="element-201" data-mp="2a60e8jmvmwy7" />
            </div>)}
        </div>
        <div className="mt-auto grid grid-cols-2 gap-2" aria-hidden="true" data-id="element-202" data-mp="2a8ctuq55wv83">
          <span className="h-10 rounded-lg border-2 border-dashed border-ink/15" data-id="element-203" data-mp="2f4jz0gpqi2oz" />
          <span className="h-10 rounded-lg border-2 border-dashed border-ink/15" data-id="element-204" data-mp="20fe4h61ud0nf" />
        </div>
      </div>
    </div>;
}